
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Users, Briefcase, User } from 'lucide-react';

interface UserContributor {
  user_email: string;
  user_id: string;
  candidate_count?: number;
  client_count?: number;
}

interface OrganizationMetric {
  organization_id: string;
  candidates_count?: number;
  clients_count?: number;
  top_contributors?: UserContributor[];
}

interface OrganizationMetricsWithUsersProps {
  organizationMetrics?: {
    top_organizations_by_candidates: OrganizationMetric[];
    top_organizations_by_clients: OrganizationMetric[];
  };
}

export const OrganizationMetricsWithUsers: React.FC<OrganizationMetricsWithUsersProps> = ({
  organizationMetrics,
}) => {
  const formatEmail = (email: string) => {
    if (email.length > 25) {
      return email.substring(0, 25) + '...';
    }
    return email;
  };

  const formatOrgId = (orgId: string) => {
    return orgId.substring(0, 8) + '...';
  };

  const renderContributors = (contributors: UserContributor[] = [], type: 'candidates' | 'clients') => {
    if (contributors.length === 0) {
      return <p className="text-xs text-muted-foreground">No contributors data available</p>;
    }

    return (
      <div className="space-y-2">
        {contributors.slice(0, 3).map((contributor, index) => (
          <div key={contributor.user_id} className="flex items-center justify-between p-2 bg-muted/20 rounded">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="w-6 h-6 flex items-center justify-center text-xs">
                {index + 1}
              </Badge>
              <div>
                <p className="text-xs font-medium" title={contributor.user_email}>
                  {formatEmail(contributor.user_email)}
                </p>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {type === 'candidates' ? contributor.candidate_count : contributor.client_count} {type}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            Top Organizations by Candidates
          </CardTitle>
          <CardDescription>
            Organizations with the most candidates and their top contributors
          </CardDescription>
        </CardHeader>
        <CardContent>
          {organizationMetrics?.top_organizations_by_candidates && organizationMetrics.top_organizations_by_candidates.length > 0 ? (
            <div className="space-y-6">
              {organizationMetrics.top_organizations_by_candidates.map((org, index) => (
                <div key={org.organization_id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="w-8 h-8 flex items-center justify-center">
                        {index + 1}
                      </Badge>
                      <div>
                        <p className="font-medium text-sm">
                          Organization {formatOrgId(org.organization_id)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {org.candidates_count} candidates
                        </p>
                      </div>
                    </div>
                    <Users className="h-4 w-4 text-blue-600" />
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium mb-2 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Top Contributors
                    </p>
                    {renderContributors(org.top_contributors, 'candidates')}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No organization candidate data available</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-green-600" />
            Top Organizations by Clients
          </CardTitle>
          <CardDescription>
            Organizations with the most clients and their top contributors
          </CardDescription>
        </CardHeader>
        <CardContent>
          {organizationMetrics?.top_organizations_by_clients && organizationMetrics.top_organizations_by_clients.length > 0 ? (
            <div className="space-y-6">
              {organizationMetrics.top_organizations_by_clients.map((org, index) => (
                <div key={org.organization_id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="w-8 h-8 flex items-center justify-center">
                        {index + 1}
                      </Badge>
                      <div>
                        <p className="font-medium text-sm">
                          Organization {formatOrgId(org.organization_id)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {org.clients_count} clients
                        </p>
                      </div>
                    </div>
                    <Briefcase className="h-4 w-4 text-green-600" />
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium mb-2 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Top Contributors
                    </p>
                    {renderContributors(org.top_contributors, 'clients')}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No organization client data available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
